import { useState } from 'react';

import { useQuery } from '@tanstack/react-query';

import type { AnalysisHistoricalResponse } from '../@types/visits';

import { makeAPIRequestReactQuery } from './api';
import { endpoints } from './endpoints';

// 30 * 5 seconds = 2.5 minutes
export const HISTORICAL_ANALYSIS_RETRIES = 30;

export interface UsePatientAnalysisHistoricalParams {
  unifiedId: string;
  primaryPracticeId: string;
  primaryOrganizationId: string;
}

/**
 * Custom React Query hook for fetching historical patient analysis data.
 * Needs an appt unifiedId, or imagingPatientId, and an orgId & practiceId
 *
 * @param {UsePatientAnalysisHistoricalParams} params - The parameters for fetching historical patient analysis.
 */
export const usePatientAnalysisHistorical = ({
  unifiedId,
  primaryPracticeId,
}: UsePatientAnalysisHistoricalParams) => {
  const path = `${endpoints.analyzeImageHistorical}`;
  const params = {
    practiceId: primaryPracticeId,
    'unifiedAppointmentIds[0]': unifiedId,
  };
  return useQuery<AnalysisHistoricalResponse>(
    [path],
    () =>
      makeAPIRequestReactQuery<AnalysisHistoricalResponse>(path, params, 'get'),
    {
      onError: () => new Error('No Images'),
    },
  );
};

/**
 * Custom React Query hook for enqueueing historical patient analysis data.
 * Needs an imagingPatientId, and a practiceId
 *
 * @param {string} imagingPatientId - The videa patient id to enqueue for analysis.
 * @param {string} primaryPracticeId - The practice id.
 */
export const useEnqueuePatientAnalysisHistorical = (
  imagingPatientId: string | null,
  primaryPracticeId: string,
  primaryOrganizationId: string,
) => {
  const path = endpoints.analyzeImageHistorical;
  const videaPatientIds = [imagingPatientId];

  return useQuery(
    [path],
    () =>
      makeAPIRequestReactQuery(path, undefined, 'post', {
        data: {
          videaPatientIds,
          practiceId: primaryPracticeId,
        },
        headers: {
          practice: primaryPracticeId,
          org: primaryOrganizationId,
        },
      }),
    {
      onError: (error) => {
        return new Error('No Images' + error);
      },
    },
  );
};

/**
 * Custom React Query hook for polling the historical patient analysis progress once enqueued.
 *
 * @param {UsePatientAnalysisHistoricalParams} params - The parameters for fetching historical patient analysis.
 * @returns {AnalysisHistoricalResponse} - The result of the query, containing the analysis data and whether the analyses are incomplete.
 */
export const usePatientAnalysisHistoricalProgress = ({
  unifiedId,
  primaryPracticeId,
  primaryOrganizationId,
}: {
  unifiedId: string;
  primaryPracticeId?: string;
  primaryOrganizationId?: string;
}) => {
  const path = `${endpoints.analyzeImageHistorical}`;
  const params = {
    practiceId: primaryPracticeId,
    'unifiedAppointmentIds[0]': unifiedId,
  };
  const [refetchInterval, setRefetchInterval] = useState(5000);
  const [refetchCount, setRefetchCount] = useState(0);

  return useQuery<AnalysisHistoricalResponse>(
    [path, params],
    () =>
      makeAPIRequestReactQuery<AnalysisHistoricalResponse>(
        path,
        params,
        'get',
        {
          headers: {
            practice: primaryPracticeId,
            org: primaryOrganizationId,
          },
        },
      ),
    {
      enabled: Boolean(unifiedId) && refetchInterval > 0,
      refetchInterval,
      onError: () => setRefetchInterval(0),
      onSuccess: () => {
        setRefetchCount(refetchCount + 1);
        if (refetchCount > HISTORICAL_ANALYSIS_RETRIES) {
          setRefetchInterval(0);
        }
      },
    },
  );
};
